<template>
  <q-card class="no-padding bg-white no-scroll full-height">
    <q-card-main class="row full-height no-padding">
      <div class="col-12 dash-number-box dash-number-box-padding relative-position">
        <div style="position: absolute; top: 10px; right: 10px; display: flex; align-items: center;">
          <q-select
            v-show="showAverage"
            v-model="averageYear"
            :placeholder="$t('year')"
            :options="averageYearOptions"
            class="q-mr-sm"
            no-parent-field
            hide-underline
          />

          <q-toggle
            v-model="showAverage"
          >
            <q-tooltip :offset="[0, 5]">{{ showAverage ? $t('hide_yearly_average') : $t('show_yearly_average') }}</q-tooltip>
          </q-toggle>
        </div>

        <h2 class="dash-number-box__heading--bold">{{ $t('statistics_revenue') }}</h2>
        <h3 class="dash-number-box__sub-heading">
          {{ $t('statistics_activity_from_to', { date1: revenueFrom, date2: revenueTo }) }}
        </h3>

        <div class="row items-start">
          <span class="dash-number-box__value on-left">{{ formatMoney(lastRevenue, currency, 0, 0) }}</span>
          <trend-hint v-if="showTrend" class="dash-number-box__value--trend" :up="!revenueIsDown" :text="trendText"/>
        </div>
        <span
          class="dash-number-box__value--sub"
          v-html="$t('statistics_revenue_month')"
        />
      </div>

      <component
        :is="chartComponent"
        class="col-12 self-end"
        :chartdata="chartdata"
        :options="chartoptions"
        :height="100"
      />
    </q-card-main>
  </q-card>
</template>

<script>
  import { formatMoney, formatPercentage } from 'src/utils/format'
  import { mapActions, mapGetters } from 'vuex'
  import Decimal from 'decimal.js'
  import Sparkline from 'components/Charts/Sparkline.vue'
  import SparklineWithAverage from 'components/Charts/SparklineWithAverage.vue'
  import TrendHint from 'components/Charts/TrendHint.vue'
  import { date } from 'quasar'
  import parseDate from 'date-fns/parse'

  export default {
    props: {
      revenue: {
        type: Array,
        required: true
      },
      currency: {
        type: String,
        required: true
      },
      average: {
        type: Object,
        required: true
      }
    },
    computed: {
      ...mapGetters(['activeTeam', 'showDashboardRevenueAverage']),
      chartComponent () {
        return this.showAverage ? 'SparklineWithAverage' : 'Sparkline'
      },
      revenueData () {
        return this.revenue.map(data => ({ value: data.value, date: data.end_date }))
      },
      revenueFrom () {
        return date.formatDate(parseDate(this.revenue[0].start_date), 'MMM YYYY')
      },
      revenueTo () {
        return date.formatDate(parseDate(this.revenue[this.revenue.length - 1].end_date), 'MMM YYYY')
      },
      lastRevenue () {
        return this.revenueData[this.revenueData.length - 1].value
      },
      secondLastRevenue () {
        return this.revenueData[this.revenueData.length - 2].value
      },
      lastSecondLastRevenueDifference () {
        return Math.abs(this.lastRevenue - this.secondLastRevenue)
      },
      showTrend () {
        return this.secondLastRevenue !== 0
      },
      revenueIsDown () {
        return this.lastRevenue < this.secondLastRevenue
      },
      trendText () {
        if (!this.showTrend) {
          return null
        }

        if (this.growth.eq(0)) {
          return '+0%'
        }

        // Take the absolute value, becasue we prepend the sign on our own
        const growthPercentage = formatPercentage(Math.abs(this.growth), 0, 1)
        const sign = this.revenueIsDown ? '-' : '+'
        const amount = this.lastSecondLastRevenueDifference

        return `${sign}${growthPercentage} (${formatMoney(amount, this.currency, 0, 0)})`
      },
      growth () {
        return new Decimal(this.lastSecondLastRevenueDifference)
          .div(new Decimal(this.secondLastRevenue))
      },
      chartdata () {
        return {
          labels: this.revenue.map(
            data => date.formatDate(parseDate(data.start_date), 'MMMM YYYY')
          ),
          datasets: [
            {
              data: this.revenue.map(data => data.value),
              borderColor: '#886ce6',
              backgroundColor: '#f8f6fd',
              pointBorderColor: '#886ce6',
              pointBackgroundColor: '#f8f6fd',
              fill: true
            }
          ]
        }
      },
      chartoptions () {
        return {
          average: {
            averageLineColor: '#d53f8c',
            averageLabelColor: '#1a202c',
            averageLabelFunction: (average) => formatMoney(average, this.currency, 0, 0),
            averageValue: this.average ? this.average.value : -1
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                return formatMoney(tooltipItem.yLabel)
              }
            }
          }
        }
      },
      showAverage: {
        get () {
          return this.showDashboardRevenueAverage
        },

        set (value) {
          this.setShowDashboardRevenueAverage(value)
        }
      },
      averageYearOptions () {
        const currentYear = new Date().getFullYear()
        const teamCreatedAt = this.activeTeam ? parseDate(this.activeTeam.created_at.date, this.$t('date_time_format')).getFullYear() : currentYear

        const values = []

        for (let i = teamCreatedAt; i <= currentYear; i++) {
          values.push({ label: i.toString(), value: i })
        }

        return values
      },
      averageYear: {
        get () {
          return this.average.options ? this.average.options.year : undefined
        },

        set (value) {
          this.$emit('averageYearChanged', value)
        }
      }
    },
    methods: {
      ...mapActions(['setShowDashboardRevenueAverage']),
      formatMoney: formatMoney
    },
    components: {
      Sparkline,
      SparklineWithAverage,
      TrendHint
    }
  }
</script>
